import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  label: string;
  value: string | number;
  hint?: string;
  delta?: { value: string; positive?: boolean };
  icon?: LucideIcon;
  accent?: "cyan" | "magenta" | "mint" | "amber" | "violet";
  className?: string;
}

const accentMap = {
  cyan: "from-[oklch(0.78_0.16_200/0.30)] to-transparent",
  magenta: "from-[oklch(0.72_0.20_320/0.30)] to-transparent",
  mint: "from-[oklch(0.80_0.18_145/0.30)] to-transparent",
  amber: "from-[oklch(0.82_0.17_80/0.30)] to-transparent",
  violet: "from-[oklch(0.70_0.18_270/0.30)] to-transparent",
};

const iconColor = {
  cyan: "text-[oklch(0.78_0.16_200)]",
  magenta: "text-[oklch(0.72_0.20_320)]",
  mint: "text-[oklch(0.80_0.18_145)]",
  amber: "text-[oklch(0.82_0.17_80)]",
  violet: "text-[oklch(0.70_0.18_270)]",
};

export function MetricCard({
  label,
  value,
  hint,
  delta,
  icon: Icon,
  accent = "cyan",
  className,
}: MetricCardProps) {
  return (
    <div className={cn("glass relative overflow-hidden rounded-2xl p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-8px_oklch(0.78_0.16_200/0.25)]", className)}>
      <div className={cn("pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-radial blur-2xl opacity-60 bg-gradient-to-br", accentMap[accent])} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground">
            {value}
          </p>
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
        {Icon && (
          <div className={cn("rounded-xl border border-white/10 bg-white/5 p-2.5 backdrop-blur", iconColor[accent])}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      {delta && (
        <div className="relative mt-3 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] font-medium">
          <span className={delta.positive ? "text-[oklch(0.80_0.18_145)]" : "text-destructive"}>
            {delta.positive ? "▲" : "▼"} {delta.value}
          </span>
        </div>
      )}
    </div>
  );
}
